import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	MAT_DIALOG_DATA,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ApprovalEnum } from '@shared/types/commonEnum';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Payment } from '@app/modules/payment/models/payment.model';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {SharePipesModule} from "@shared/share-pipes/share-pipes.module";
import {PaymentOfJobApi} from "@app/modules/payment/api/payment-of-job.api";
import {SnackBarService} from "@shared/services/snack-bar.service";
import {EMPTY, tap, catchError} from "rxjs";
import {ShareDirectivesModule} from "@shared/share-directives/share-directives.module";
import { PaymentService } from '@shared/component/payments/services/payment.service';

type DialogData = {
    action: ApprovalEnum;
    payment: Payment;
};

@Component({
    selector: 'app-payment-approval-dialog',
    standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    SharePipesModule,
    ShareDirectivesModule,
  ],
    templateUrl: './payment-approval-dialog.component.html',
    styleUrls: ['./payment-approval-dialog.component.scss'],
})
export class PaymentApprovalDialogComponent implements OnInit {
    protected currentDialogMode: 'viewDetails' | 'enterReason'; // 'viewDetails' is initial state, 'enterReason' for decline reason

    protected readonly ApprovalEnum = ApprovalEnum;

    payment: Payment;

    reasonInput = new FormControl('');

    constructor(
        @Inject(MAT_DIALOG_DATA) protected readonly data: DialogData,
        private readonly dialogRef: MatDialogRef<PaymentApprovalDialogComponent>,
        private readonly paymentOfJobApi: PaymentOfJobApi,
        private readonly snackbarService: SnackBarService,
        private readonly paymentService: PaymentService,
    ) {
    }

    ngOnInit(): void {
        // Initialize based on provided action, default to 'viewDetails'
        this.currentDialogMode = this.data.action === ApprovalEnum.Decline ? 'enterReason' : 'viewDetails';
        this.payment = this.data.payment;
    }

    onApproveClicked(): void {
        this.onConfirmAction(ApprovalEnum.Approve);
    }

    onDeclineClicked(): void {
        if (this.currentDialogMode === 'viewDetails') {
            this.currentDialogMode = 'enterReason';
        } else {
            this.onConfirmAction(ApprovalEnum.Decline);
        }
    }

    onCancelClicked(): void {
        if (this.currentDialogMode === 'viewDetails') {
            this.dialogRef.close(false);
        } else {
            this.currentDialogMode = 'viewDetails';
        }
    }

    openProofOfPayment(): void {
        this.paymentService.openProofOfPayment(this.payment);
    }

    canViewProofOfPayment(): boolean {
        return this.payment?.canViewProofOfPayment();
    }

    onConfirmAction(action: ApprovalEnum): void {
        if (action === ApprovalEnum.Approve) {
            this.paymentOfJobApi.approvePayment$(this.payment.id).pipe(
                tap((result) => {
                    if (result === true) {
                        this.snackbarService.pushSuccessAlert('Payment has been approved!');
                        this.dialogRef.close(true);
                    } else {
                        this.snackbarService.pushErrorMessage();
                        this.dialogRef.close(false);
                    }
                }),
                catchError(() => {
                    this.snackbarService.pushErrorMessage();
                    this.dialogRef.close(false);
                    return EMPTY;
                })
            ).subscribe();
        } else if (action === ApprovalEnum.Decline) {
            this.paymentOfJobApi
                .rejectPayment(this.payment.id, this.reasonInput.value)
                .pipe(
                    tap((result) => {
                        if (result === true) {
                            this.snackbarService.pushErrorAlert('Payment declined!', 'Opps!');
                            this.dialogRef.close(true);
                        } else {
                            this.snackbarService.pushErrorMessage();
                            this.dialogRef.close(false);
                        }
                    }),
                    catchError(() => {
                        this.snackbarService.pushErrorMessage();
                        this.dialogRef.close(false);
                        return EMPTY;
                    })
                ).subscribe();
        }
    }
}
